if (!customElements.get('cavador-flooring-studio-comparison')) {
  customElements.define('cavador-flooring-studio-comparison', class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      const range = this.querySelector('[data-cfsc-range]');
      const original = this.querySelector('[data-cfsc-before]');
      const previewWrap = this.querySelector('[data-cfsc-after]');
      const preview = previewWrap?.querySelector('img');
      const motionButton = this.querySelector('[data-cfsc-motion]');
      const caption = this.querySelector('[data-cfsc-caption]');
      const placeholder = this.querySelector('[data-cfsc-placeholder]');
      const hint = this.querySelector('[data-cfsc-hint]');
      const beforeLabel = this.querySelector('[data-cfsc-label-before]');
      const afterLabel = this.querySelector('[data-cfsc-label-after]');
      const divider = this.querySelector('[data-cfsc-divider]');
      if (!range || !original || !preview || !motionButton) return;
      const media = matchMedia('(prefers-reduced-motion: reduce)');
      const controller = new AbortController();
      const options = { signal: controller.signal };
      const initialCaption = caption?.textContent || '';
      let requested = this.motionRequested ?? (this.dataset.autoplay !== 'false' && !media.matches);
      let visible = typeof IntersectionObserver !== 'function';
      let ready = false;
      let failed = false;
      let frame = 0;
      let started = null;
      let origin = 50;
      let phase = 0;
      let dragging = false;

      const draw = value => {
        value = Number.isFinite(Number(value)) ? Math.max(0, Math.min(100, Number(value))) : 50;
        range.value = String(value);
        this.style.setProperty('--cfsc-reveal', `${value}%`);
        range.setAttribute('aria-valuetext', (this.dataset.valueTemplate || '[percent]%')
          .replace('[percent]', String(Math.round(value)))
          .replace('[after]', String(Math.round(100 - value))));
        if (beforeLabel) beforeLabel.style.visibility = ready && value < 18 ? 'hidden' : 'visible';
        if (afterLabel) afterLabel.style.visibility = value > 82 ? 'hidden' : 'visible';
      };
      const stopFrame = () => {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        started = null;
      };
      const tick = time => {
        if (controller.signal.aborted) return;
        if (started === null) {
          started = time;
          origin = Number(range.value);
          phase = Math.asin(Math.max(-1, Math.min(1, (origin - 50) / 25)));
        }
        const elapsed = time - started;
        const settle = origin < 25 || origin > 75 ? 700 : 0;
        if (elapsed < settle) {
          const progress = elapsed / settle;
          const eased = progress * progress * (3 - 2 * progress);
          draw(origin + (Math.max(25, Math.min(75, origin)) - origin) * eased);
        } else {
          draw(50 + 25 * Math.sin(phase + (elapsed - settle) * Math.PI * 2 / 7000));
        }
        frame = requestAnimationFrame(tick);
      };
      const refresh = () => {
        if (controller.signal.aborted) return;
        this.motionRequested = requested;
        motionButton.hidden = !ready || media.matches;
        motionButton.disabled = !ready;
        motionButton.textContent = requested ? this.dataset.pauseLabel : this.dataset.playLabel;
        this.dataset.playing = String(ready && requested && !media.matches && visible && !document.hidden);
        if (ready && requested && !media.matches && visible && !document.hidden) {
          if (!frame) frame = requestAnimationFrame(tick);
        } else {
          stopFrame();
        }
      };
      const pause = () => {
        requested = false;
        refresh();
      };
      const fail = () => {
        if (controller.signal.aborted) return;
        failed = true;
        ready = false;
        requested = false;
        dragging = false;
        previewWrap.hidden = true;
        range.hidden = true;
        range.disabled = true;
        if (divider) divider.hidden = true;
        if (afterLabel) afterLabel.hidden = true;
        if (hint) hint.hidden = true;
        const originalReady = original.complete && original.naturalWidth > 0;
        original.hidden = !originalReady;
        if (placeholder) placeholder.hidden = originalReady;
        if (beforeLabel) { beforeLabel.hidden = !originalReady; beforeLabel.style.visibility = 'visible'; }
        if (caption) caption.textContent = originalReady ? this.dataset.fallbackCaption : this.dataset.unavailableCaption;
        this.dataset.pairState = 'unavailable';
        refresh();
      };
      const loadPair = () => {
        if (controller.signal.aborted) return;
        if (failed) { fail(); return; }
        if (original.complete && !original.naturalWidth || preview.complete && !preview.naturalWidth) { fail(); return; }
        if (original.complete && original.naturalWidth && !preview.complete) preview.loading = 'eager';
        if (!original.complete || !preview.complete || !original.naturalWidth || !preview.naturalWidth) return;
        ready = true;
        original.hidden = false;
        if (placeholder) placeholder.hidden = true;
        previewWrap.hidden = false;
        range.hidden = false;
        range.disabled = false;
        if (divider) divider.hidden = false;
        if (afterLabel) afterLabel.hidden = false;
        if (beforeLabel) beforeLabel.hidden = false;
        if (hint) hint.hidden = false;
        if (caption) caption.textContent = initialCaption;
        this.dataset.pairState = 'ready';
        draw(Number(range.value));
        refresh();
      };
      const compareAt = event => {
        const bounds = range.getBoundingClientRect();
        if (bounds.width) draw((event.clientX - bounds.left) / bounds.width * 100);
      };
      range.addEventListener('pointerdown', event => {
        if (!ready || event.button !== 0 || event.isPrimary === false) return;
        pause();
        dragging = true;
        range.focus({ preventScroll: true });
        range.setPointerCapture?.(event.pointerId);
        compareAt(event);
        event.preventDefault();
      }, options);
      range.addEventListener('pointermove', event => {
        if (!dragging) return;
        compareAt(event);
        event.preventDefault();
      }, options);
      const endDrag = () => { dragging = false; };
      range.addEventListener('pointerup', endDrag, options);
      range.addEventListener('pointercancel', endDrag, options);
      range.addEventListener('lostpointercapture', endDrag, options);
      range.addEventListener('focus', pause, options);
      range.addEventListener('keydown', pause, options);
      range.addEventListener('input', () => { pause(); draw(Number(range.value)); }, options);
      motionButton.addEventListener('click', () => {
        if (!ready || media.matches) return;
        requested = !requested;
        refresh();
      }, options);
      original.addEventListener('load', loadPair, options);
      preview.addEventListener('load', loadPair, options);
      original.addEventListener('error', fail, options);
      preview.addEventListener('error', fail, options);
      original.addEventListener('dragstart', event => event.preventDefault(), options);
      preview.addEventListener('dragstart', event => event.preventDefault(), options);
      document.addEventListener('visibilitychange', refresh, options);
      media.addEventListener('change', () => {
        if (media.matches) requested = false;
        refresh();
      }, options);
      const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(([entry]) => {
        visible = !!entry?.isIntersecting && entry.intersectionRatio >= .25;
        refresh();
      }, { threshold: [0, .25] }) : null;
      observer?.observe(this);
      this.cleanup = () => {
        controller.abort();
        observer?.disconnect();
        dragging = false;
        stopFrame();
      };
      this.dataset.pairState = 'loading';
      draw(Number(range.value));
      refresh();
      loadPair();
    }

    disconnectedCallback() {
      this.cleanup?.();
      this.cleanup = null;
    }
  });
}
